import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Search,
  MoreVert,
  Visibility,
  Delete,
  Refresh,
  Add,
  CheckCircle,
  Cancel,
  Warning,
  Settings,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDevices, useDeleteDevice, useRegenerateDeviceToken } from '../../hooks/useFleet';
import type { Device } from '../../api/types';
import { SectionTabs } from '../../components/SectionTabs';
import { fleetHelp } from '../../components/PageHelp/helpContent';
import { FLEET_TABS, FLEET_ACCENT_COLOR } from './fleetTabs';
import { useOrgFilter } from '../../contexts/OrganizationContext';

const DevicesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);

  // Use global organization filter
  const orgFilter = useOrgFilter();

  const { data: devicesData, isLoading, error } = useDevices({
    page: page + 1,
    size: rowsPerPage,
    status: statusFilter || undefined,
    organization_id: orgFilter,
  });

  const deleteDevice = useDeleteDevice();
  const regenerateToken = useRegenerateDeviceToken();

  const devices = devicesData?.data || [];
  const total = devicesData?.total || 0;

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status);
    setPage(0);
    if (status) {
      setSearchParams({ status });
    } else {
      setSearchParams({});
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, device: Device) => {
    setAnchorEl(event.currentTarget);
    setSelectedDevice(device);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedDevice(null);
  };

  const handleViewDevice = () => {
    if (selectedDevice) {
      navigate(`/fleet/devices/${selectedDevice.id}`);
    }
    handleMenuClose();
  };

  const handleDeleteDevice = async () => {
    if (selectedDevice && window.confirm(`Delete device "${selectedDevice.name}"?`)) {
      try {
        await deleteDevice.mutateAsync(selectedDevice.id);
      } catch (error) {
        console.error('Failed to delete device:', error);
      }
    }
    handleMenuClose();
  };

  const handleRegenerateToken = async () => {
    if (selectedDevice && window.confirm(`Regenerate provisioning token for "${selectedDevice.name}"? This will require re-provisioning the device.`)) {
      try {
        await regenerateToken.mutateAsync(selectedDevice.id);
      } catch (error) {
        console.error('Failed to regenerate token:', error);
      }
    }
    handleMenuClose();
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'online': return <CheckCircle sx={{ color: '#10b981', fontSize: 18 }} />;
      case 'offline': return <Cancel sx={{ color: '#ef4444', fontSize: 18 }} />;
      case 'error': return <Warning sx={{ color: '#f59e0b', fontSize: 18 }} />;
      case 'provisioning': return <Settings sx={{ color: '#6366f1', fontSize: 18 }} />;
      default: return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return { bg: '#10b98120', color: '#10b981' };
      case 'offline': return { bg: '#ef444420', color: '#ef4444' };
      case 'error': return { bg: '#f59e0b20', color: '#f59e0b' };
      case 'provisioning': return { bg: '#6366f120', color: '#6366f1' };
      default: return { bg: '#6b728020', color: '#6b7280' };
    }
  };

  const formatLastSeen = (timestamp?: string) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hr ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  const filteredDevices = search
    ? devices.filter(
        (d: Device) =>
          d.name?.toLowerCase().includes(search.toLowerCase()) ||
          d.serial_number?.toLowerCase().includes(search.toLowerCase()) ||
          d.site_name?.toLowerCase().includes(search.toLowerCase())
      )
    : devices;

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        Failed to load devices: {(error as Error)?.message || 'Unknown error'}
      </Alert>
    );
  }

  return (
    <Box>
      <SectionTabs
        title="Fleet Management"
        tabs={FLEET_TABS}
        accentColor={FLEET_ACCENT_COLOR}
        help={fleetHelp.devices}
        actionButton={{
          label: 'Add Device',
          icon: <Add />,
          onClick: () => navigate('/fleet/provisioning'),
        }}
      />

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search devices..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => handleStatusFilterChange(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="online">Online</MenuItem>
              <MenuItem value="offline">Offline</MenuItem>
              <MenuItem value="error">Error</MenuItem>
              <MenuItem value="provisioning">Provisioning</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Devices Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Device</TableCell>
                <TableCell>Serial Number</TableCell>
                <TableCell>Site</TableCell>
                <TableCell>Organization</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Seen</TableCell>
                <TableCell width={60}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : filteredDevices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No devices found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredDevices.map((device: Device) => {
                  const statusColors = getStatusColor(device.status);
                  return (
                    <TableRow
                      key={device.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/fleet/devices/${device.id}`)}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getStatusIcon(device.status)}
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {device.name}
                            </Typography>
                            {device.model && (
                              <Typography variant="caption" color="text.secondary">
                                {device.model}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {device.serial_number}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {device.site_name || '-'}
                        </Typography>
                        {device.site_city && (
                          <Typography variant="caption" color="text.secondary">
                            {device.site_city}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {device.organization_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={device.status}
                          size="small"
                          sx={{
                            bgcolor: statusColors.bg,
                            color: statusColors.color,
                            textTransform: 'capitalize',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatLastSeen(device.last_heartbeat)}
                        </Typography>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, device)}
                        >
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewDevice}>
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleRegenerateToken}>
          <ListItemIcon>
            <Refresh fontSize="small" />
          </ListItemIcon>
          <ListItemText>Regenerate Token</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteDevice} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <Delete fontSize="small" sx={{ color: 'error.main' }} />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default DevicesPage;
